import type { FormularDFormData } from '../../../types/reports.js';
import { CEMENT_GROUPS, STRENGTH_CLASSES, section2aFields, section2bFields, section2cFields } from './catalog.js';

/**
 * Reine Client-Summenfunktionen fuer Formular D (Zementartenstatistik), aus `FormularDForm.tsx`
 * ausgelagert (Plan 23-02 Task 2). Fachliche Aenderung gegenueber dem vorherigen Stand (D-12):
 * jede Funktion liefert `null`, solange kein einziger Eingabewert gesetzt ist -- sobald einer
 * gesetzt ist, bleibt das Ergebnis rechnerisch identisch zu vorher (`toFixed(2)`).
 */

/**
 * Zielzustand ist die Server-Anzeige (`FormularDCalculator`, Plan 23-01) -- dann faellt
 * `DerivedTotal` ersatzlos weg. Der Nutzer misst die Traegheit selbst (D-27); DIESE eine
 * Konstante entscheidet, welcher Weg die Anzeige speist (D-26).
 *
 * Rueckweg in zwei Stufen, falls der Nutzer sich nach seiner eigenen Messung (D-27) gegen die
 * Server-Anzeige entscheidet:
 * - **Stufe 1 (Anzeige):** `USE_SERVER_CALC` auf `false` setzen -- die Summen kommen wieder aus
 *   `DerivedTotal`; eine Zeile, sonst nichts. Der Netzverkehr (POST /api/reports/calculate je
 *   Eingabepause) bleibt dabei bestehen, weil er an `calcInputKeys` haengt, nicht an der Anzeige.
 * - **Stufe 2 (Netzverkehr):** zusaetzlich `calcInputKeys` am FormularD-Deskriptor in
 *   `reportTypes.ts` wieder entfernen -- dann feuert `useReportCalc` beim Tippen keinen Request
 *   mehr und `ReportEditPage` rendert wieder nicht pro Tastendruck neu.
 */
export const USE_SERVER_CALC = true;

/**
 * Form der Antwort von `POST /api/reports/calculate` fuer `formular_d`
 * (`FormularDCalculator::calculate()`, Plan 23-01). Gueltige Schluessel stehen in
 * `FORMULAR_D_CALC_OUTPUT_KEYS` (catalog.ts) und sind dort per Test festgenagelt.
 */
export type FormularDCalculations = Readonly<Record<string, string | null>>;

// Helper to safely get a numeric value from a form.getValues() snapshot. Returns null if the
// field carries no value at all (D-12's "hasAny" signal), 0 for a real zero.
function getNumericValue(values: FormularDFormData, key: string): number | null {
  const value = values[key as keyof FormularDFormData];
  if (value === null || value === undefined || value === '') return null;
  const parsed = parseFloat(String(value));
  return Number.isNaN(parsed) ? null : parsed;
}

function sumWithNullSemantics(values: (number | null)[]): string | null {
  const hasAny = values.some((v) => v !== null);
  if (!hasAny) return null;
  return values.reduce((sum: number, v) => sum + (v ?? 0), 0).toFixed(2);
}

export function calculateTypeTotal(values: FormularDFormData, prefix: string): string | null {
  return sumWithNullSemantics(STRENGTH_CLASSES.map((suffix) => getNumericValue(values, `${prefix}_${suffix}`)));
}

export function calculateGroupTotal(values: FormularDFormData, groupId: string): string | null {
  const group = CEMENT_GROUPS.find((g) => g.id === groupId);
  if (!group) return null;
  return sumWithNullSemantics(
    group.types.map((type) => {
      const typeTotal = calculateTypeTotal(values, type.prefix);
      return typeTotal === null ? null : parseFloat(typeTotal);
    }),
  );
}

export function calculateSectionITotal(values: FormularDFormData): string | null {
  return sumWithNullSemantics(
    CEMENT_GROUPS.map((group) => {
      const groupTotal = calculateGroupTotal(values, group.id);
      return groupTotal === null ? null : parseFloat(groupTotal);
    }),
  );
}

export function calculateSection2Total(values: FormularDFormData, fields: { key: string }[]): string | null {
  return sumWithNullSemantics(fields.map((f) => getNumericValue(values, f.key)));
}

export function calculateGrandTotal(values: FormularDFormData): string | null {
  const sectionI = calculateSectionITotal(values);
  const section2a = calculateSection2Total(values, section2aFields);
  const section2b = calculateSection2Total(values, section2bFields);
  const section2c = calculateSection2Total(values, section2cFields);
  const klinkermehl = getNumericValue(values, 'klinkermehl');

  return sumWithNullSemantics([
    sectionI === null ? null : parseFloat(sectionI),
    section2a === null ? null : parseFloat(section2a),
    section2b === null ? null : parseFloat(section2b),
    section2c === null ? null : parseFloat(section2c),
    klinkermehl,
  ]);
}

/**
 * D-20: dieselbe Formel wie `FormularDCalculator::calculate()` --
 * Abschnitt-I-Summe + zugelassene (II.a) + E 4 und E4-RS aus II.c + Klinkermehl. Putz (II.b) sowie
 * E 2 und E 3 sind ausdruecklich NICHT enthalten (BDZ-Vorstandsbeschluss vom 14.11.2000). Speist die Anzeige des in Plan 23-06 gesperrten
 * `beitragspflichtig`-Feldes, solange `USE_SERVER_CALC` auf `false` steht.
 */
// Spiegel von FormularDFieldCatalog::BEITRAGSPFLICHTIG_TRAGSCHICHT_FIELDS (Backend); nur Fallback,
// solange USE_SERVER_CALC=false.
const BEITRAGSPFLICHTIG_TRAGSCHICHT_KEYS: readonly string[] = ['tragschicht_E4', 'tragschicht_E4_RS'];

export function calculateBeitragspflichtig(values: FormularDFormData): string | null {
  const sectionI = calculateSectionITotal(values);
  const zugelassene = calculateSection2Total(values, section2aFields);
  const tragschicht = calculateSection2Total(
    values,
    section2cFields.filter((f) => BEITRAGSPFLICHTIG_TRAGSCHICHT_KEYS.includes(f.key)),
  );
  const klinkermehl = getNumericValue(values, 'klinkermehl');

  return sumWithNullSemantics([
    sectionI === null ? null : parseFloat(sectionI),
    zugelassene === null ? null : parseFloat(zugelassene),
    tragschicht === null ? null : parseFloat(tragschicht),
    klinkermehl,
  ]);
}
